/**
 * @module config/navGroups
 * @description Declarative source of truth for the main sidebar
 * (NavigationDrawer.vue). The FIRST LEVEL is the kind of work, not the module
 * (Maurizio, 2026-09-08): "Data entry" (daily and monthly operations), "Data
 * review" (Warehouse, GL), then the remaining "Modules" exactly as before.
 *
 * Item shapes:
 *   - "subheader": a section label — `{ type: "subheader", key, title }`.
 *     Non-clickable; NavigationDrawer.vue renders it as a v-list-subheader
 *     (a thin divider in the icon-only rail) and drops it when nothing visible
 *     follows it before the next label.
 *   - "divider": a plain rule — `{ type: "divider", key }`. A v-divider in both
 *     drawer states; dropped like a label when nothing visible follows it.
 *     Home.vue ignores it (Maurizio, 2026-09-10: a rule before Fusion Eat).
 *   - "leaf": a standalone single click target (Home, Assets, ...).
 *   - "group": a parent row + children shown once the drawer is hover-expanded.
 *       WITH `path`    → the header navigates to that page and the group expands
 *                        because the current route is inside it (route-derived,
 *                        as before).
 *       WITHOUT `path` → an ACCORDION: the header toggles its children and
 *                        lands on its LEAD entry — the first child this user
 *                        can see (2026-09-11; before that it never navigated).
 *                        Used where the children are actions rather than pages
 *                        (the four warehouse dialog shortcuts share the
 *                        Inventory route and carry no routeNames), so
 *                        route-derived expansion alone could never open it.
 *                        Visible only if at least one child is (isGroupVisible).
 *
 * Schema (leaf/group/child, `children` only on a group):
 *   title, subtitle? (one line drawn under the title in the expanded drawer and
 *   appended to the rail tooltip — the page the header opens, e.g. "Inventory";
 *   set per group as Maurizio asks — Warehouse, GL, Sales, Menu, Daily and
 *   Monthly operations so far, 2026-09-10),
 *   icon, iconColor?, path, routeNames? (string[], for active-state
 *   matching — omit on pure actions), currentApp? (sets useApp().currentApp on
 *   click; optional), appGate? (string|string[], ANY-match against
 *   allowed_apps), requiresAppAdmin? (true = appadmin only, e.g. Settings),
 *   featureFlag? (checked via isFeatureEnabled(), e.g. "tasks").
 *
 * Module icons and colours come from config/moduleVisuals.js (MODULES.<key>.icon
 * for a module's header/leaf, moduleColor("<key>") for every entry of that
 * module — children keep their own feature icon but take the module colour).
 * Only the two path-less accordions (Daily / Monthly operations) and the
 * Administration entries keep literal colours: they are not modules. Every icon
 * is registered in mdiIcons.js because it already renders on those pages.
 *
 * IMPORTANT — access-point check: before adding a route as a GROUP CHILD,
 * confirm its own appGate is reachable given the group's visibility. For an
 * ACCORDION group this is safe by construction — children decide the group's
 * visibility, so a cashier holding only "direct-upload" sees "Daily operations"
 * with just Direct Upload under it (that entry used to be kept as a standalone
 * leaf for exactly this reason). For a group WITH a path, isGroupVisible() uses
 * OR-logic (parent's own gate OR any child's gate), but a narrower audience for
 * the group's own page than for a child can still produce a visible parent with
 * zero visible children — acceptable (a journal-only user sees "GL" with no
 * children to hover into), just something to be aware of when adding a group.
 */
import { isFeatureEnabled } from "./featureFlags.js";
import { MODULES, moduleColor } from "./moduleVisuals.js";
import { holdsApp, canEditApp, canReadPayroll } from "@/utils/appGrants.js";

export const navGroups = [
  {
    type: "leaf",
    key: "home",
    title: "Home",
    icon: "mdi-home",
    path: "/home",
    routeNames: ["Home"],
  },

  // ── Data entry ─────────────────────────────────────────────────────────────
  { type: "subheader", key: "sh-entry", title: "Data entry" },

  // Everything an operator keys in day to day. Accordion (no path): the header
  // toggles the list; each child keeps its own gate, so a warehouse-ops kitchen
  // operator sees only the four stock entries, a cashier only Direct Upload.
  {
    type: "group",
    key: "daily",
    title: "Daily operations",
    // Fallback subtitle only. Since 2026-09-11 the drawer shows — and the
    // header lands on — the first child THIS user can see (Delivery for a
    // warehouse admin, Stock Out for a kitchen operator, Direct Upload for a
    // cashier), so the gates decide the landing, not this value.
    subtitle: "Delivery",
    icon: "mdi-pencil",
    iconColor: "amber",
    children: [
      {
        title: "Delivery",
        icon: "mdi-truck-delivery",
        iconColor: moduleColor("warehouse"),
        appGate: "warehouse",
        currentApp: "warehouse",
        path: "/accounting/delivery",
        routeNames: ["Delivery"],
      },
      {
        title: "Journal entry",
        editOnly: true, // typing work — hidden from those who may only look
        icon: "mdi-book-edit-outline",
        iconColor: moduleColor("accounting"),
        appGate: ["journal", "gl"],
        currentApp: "journal",
        path: "/accounting/journal",
        routeNames: ["Journal"],
      },
      {
        title: "Upload documents",
        editOnly: true, // typing work — hidden from those who may only look
        subtitle: "Scans from the phone",
        icon: "mdi-file-upload-outline",
        iconColor: moduleColor("accounting"),
        appGate: ["journal", "gl", "warehouse"],
        currentApp: "journal",
        path: "/accounting/documents/upload",
        routeNames: ["UploadDocuments"],
      },
      {
        title: "Direct Upload",
        editOnly: "grant", // a full grant, any role (cashiers are plain users)
        icon: MODULES.directUpload.icon,
        iconColor: moduleColor("directUpload"),
        appGate: ["direct-upload", "sales", "accounting"],
        currentApp: "accounting",
        path: "/accounting/direct-upload",
        routeNames: ["DirectUpload"],
      },
      // Settlements sat at the bottom of Accounting (Data review) until 2026-09-21,
      // and in that time not one cheque was issued and not one payout settled in
      // the system, while the bank statement showed about two supplier cheques a
      // week. It is entry work, done when the cheque is written — not a month-end
      // chore, which is why it was NOT folded into the Month End wizard: that
      // screen is opened once a month, for one month. The Settle step there still links here.
      // Named for what a person looks for — the cheque, the bank — with the
      // accountant's word underneath, so the two are seen together once and the
      // help pages' "Settlements" still means something (Maurizio, 2026-09-21).
      {
        title: "Cheques & bank",
        subtitle: "Settlements",
        icon: "mdi-swap-horizontal",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        // Opens on the cheques in circulation (Maurizio, 2026-10-17); Payables,
        // Receivables and the bank import are one button away on that page.
        path: "/accounting/settlements/cheques",
        routeNames: ["Settlements", "ChequesInCirculation"],
      },
      // The four warehouse dialogs live on the Inventory page and open through a
      // query flag it watches (WarehouseInventoryView.vue: ?out=1, ?wastage=1,
      // ?transfer=1, ?count=1). Deliberately NO routeNames: they are actions,
      // not pages — once the dialog closes you are on Inventory, and it is the
      // Warehouse (review) group that should light up, not this one.
      {
        title: "Stock Out",
        editOnly: true, // typing work — hidden from those who may only look
        icon: "mdi-export",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse?out=1",
      },
      {
        title: "Wastage",
        editOnly: true, // typing work — hidden from those who may only look
        icon: "mdi-trash-can-outline",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse?wastage=1",
      },
      {
        title: "Transfer",
        editOnly: true, // typing work — hidden from those who may only look
        icon: "mdi-swap-horizontal",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse?transfer=1",
      },
      {
        title: "Stock Count",
        editOnly: true, // typing work — hidden from those who may only look
        icon: "mdi-clipboard-list-outline",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse?count=1",
      },
    ],
  },

  {
    type: "group",
    key: "monthly",
    title: "Monthly operations",
    // Fallback subtitle only — the drawer shows, and the header lands on, the
    // first child this user can see (Month End, or Payroll for an hr-payroll
    // user who has no gl grant). See NavigationDrawer.vue's leadEntry().
    subtitle: "Month End",
    icon: "mdi-calendar-month",
    iconColor: "teal", // a job, not a module — literal on purpose (see moduleVisuals.js)
    children: [
      // Month End spans the warehouse (count, consumption, transfers), the GL
      // (postings, settlements, period lock) and the platform data (coverage) —
      // a job, not a GL sub-page (Maurizio, 2026-09-08). Gated on gl; the route
      // adds requiresAdmin, so a gl viewer sees the entry and is sent to
      // AccessDenied by the guard (same as before).
      {
        title: "Month End",
        icon: "mdi-calendar-check",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/month-end",
        routeNames: ["MonthEnd"],
      },
      // Also listed under HR and Contacts. This copy comes first in the array,
      // so on /hr/payroll it is Monthly operations that expands (the drawer's
      // activeGroupKey is find()-first); the HR header still highlights.
      {
        title: "Payroll",
        payroll: true, // salaries on screen: payroll readers only
        icon: MODULES.payroll.icon,
        iconColor: moduleColor("payroll"),
        appGate: ["hr-payroll", "accounting", "gl"],
        currentApp: "hr",
        path: "/hr/payroll",
        routeNames: ["HrPayroll"],
      },
    ],
  },

  // ── Data review ────────────────────────────────────────────────────────────
  { type: "subheader", key: "sh-review", title: "Data review" },

  {
    type: "group",
    key: "warehouse",
    title: "Warehouse",
    // The page the header opens (Maurizio, 2026-09-10).
    subtitle: "Inventory",
    icon: MODULES.warehouse.icon,
    iconColor: moduleColor("warehouse"),
    appGate: ["warehouse", "warehouse-ops"],
    currentApp: "warehouse",
    path: "/accounting/warehouse",
    routeNames: ["WarehouseInventory"],
    // Kitchen operators (warehouse-ops) see the stock views. Delivery moved to
    // Daily operations (it is data entry); Items stays here — the catalogue is
    // the full warehouse job, not a kitchen one.
    children: [
      // The group's own page, listed explicitly like every other entry
      // (Maurizio, 2026-09-10: "for consistency … as in Daily operations").
      {
        title: "Inventory",
        icon: "mdi-warehouse",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse",
        routeNames: ["WarehouseInventory"],
      },
      {
        title: "Stock Journal",
        icon: "mdi-book-open-page-variant",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse/journal",
        routeNames: ["WarehouseJournal"],
      },
      {
        title: "Stock Ledger",
        icon: "mdi-book-open-variant",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse/ledger",
        routeNames: ["WarehouseLedger"],
      },
      {
        title: "Par Stock",
        icon: "mdi-chart-bar",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse/par-stock",
        routeNames: ["WarehouseParStock"],
      },
      {
        title: "Monthly Summary",
        icon: "mdi-file-chart-outline",
        iconColor: moduleColor("warehouse"),
        appGate: ["warehouse", "warehouse-ops"],
        currentApp: "warehouse",
        path: "/accounting/warehouse/monthly-summary",
        routeNames: ["WarehouseMonthlySummary"],
      },
      {
        title: "Items",
        icon: "mdi-format-list-bulleted",
        iconColor: moduleColor("warehouse"),
        appGate: "warehouse",
        currentApp: "warehouse",
        path: "/accounting/items",
        routeNames: ["Items"],
      },
    ],
  },

  // "GL" — the header is the Journal page itself, opened to LOOK: this is the
  // review half of the sidebar, so it lands on the search view (?find=1), like
  // its Journal child. Writing an entry is Daily operations → Journal entry.
  {
    type: "group",
    key: "gl",
    title: "Accounting",
    // The page the header opens (Maurizio, 2026-09-10).
    subtitle: "Journal — find an entry",
    icon: MODULES.accounting.icon,
    iconColor: moduleColor("accounting"),
    appGate: ["journal", "gl"],
    currentApp: "journal",
    path: "/accounting/journal?find=1",
    routeNames: ["Journal"],
    children: [
      // The group's own page, listed explicitly like every other entry
      // (Maurizio, 2026-09-10: "for consistency … as in Daily operations").
      {
        // Data REVIEW: the same page, opened to look something up. ?find=1 puts a
        // search panel where the entry form stands on the Data entry path
        // (JournalView.vue) — arriving here to find something and being given a
        // form for writing a new entry was the wrong tool first (Maurizio,
        // 2026-09-22).
        title: "Journal",
        subtitle: "find an entry",
        icon: "mdi-book-open-page-variant",
        iconColor: moduleColor("accounting"),
        appGate: ["journal", "gl"],
        currentApp: "journal",
        path: "/accounting/journal?find=1",
        routeNames: ["Journal"],
      },
      {
        title: "Ledger",
        icon: "mdi-book-open-variant",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/ledger",
        routeNames: ["Ledger"],
      },
      {
        title: "Cash Daybook",
        icon: "mdi-cash-register",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/cash-daybook",
        routeNames: ["CashDaybook"],
      },
      {
        title: "Chart of Accounts",
        icon: "mdi-file-tree",
        iconColor: moduleColor("accounting"),
        appGate: ["gl-config", "gl", "accounting"], // GL readers look; gl-config / accounting admins change it
        currentApp: "journal",
        path: "/accounting/coa",
        routeNames: ["Coa"],
      },
      {
        title: "Trial Balance",
        icon: "mdi-scale-balance",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/trial-balance",
        routeNames: ["TrialBalance"],
      },
      {
        title: "Financials",
        icon: "mdi-file-document-outline",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/financials",
        routeNames: ["Financials"],
      },
      {
        title: "Food cost %",
        icon: "mdi-chart-line",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/food-cost",
        routeNames: ["FoodCostRatios"],
      },
      {
        title: "Cost centers",
        icon: "mdi-sitemap",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/cost-centers",
        routeNames: ["CostCenters"],
      },
      {
        title: "VAT return",
        icon: "mdi-receipt-text-outline",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/vat-return",
        routeNames: ["VatReturn"],
      },
      {
        title: "Periods",
        icon: "mdi-calendar-lock",
        iconColor: moduleColor("accounting"),
        appGate: ["gl-config", "gl", "accounting"], // GL readers look; gl-config / accounting admins change it
        currentApp: "journal",
        path: "/accounting/periods",
        routeNames: ["Periods"],
      },
      {
        title: "Fixed Assets",
        subtitle: "Book depreciation", // shown under the title in the sidebar (Maurizio, 2026-11-01)
        icon: "mdi-domain",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/fixed-assets",
        routeNames: ["FixedAssets"],
      },
      {
        title: "Paid in advance",
        subtitle: "Book provisions", // shown under the title in the sidebar (Maurizio, 2026-11-01)
        icon: "mdi-calendar-range",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/prepaid",
        routeNames: ["PrepaidExpenses"],
      },
      {
        title: "Staff provisions",
        payroll: true, // per person, from salaries: payroll readers only
        icon: "mdi-account-cash",
        iconColor: moduleColor("accounting"),
        appGate: "gl",
        currentApp: "journal",
        path: "/accounting/staff-provisions",
        routeNames: ["StaffProvisions"],
      },
      // No "Platform Cost Coverage" entry here: it is step 2 of the Month End
      // wizard (Monthly operations) and is reached from there.
      // No "Settlements" either — it is data ENTRY, not review, and lives under
      // Daily operations (see there).
    ],
  },

  // ── Modules — exactly as before ─────────────────────────────────────────────
  { type: "subheader", key: "sh-modules", title: "Modules" },

  {
    type: "group",
    key: "sales",
    title: "Sales",
    // The page the header opens (Maurizio, 2026-09-10).
    subtitle: "Sales Analytics",
    icon: MODULES.sales.icon,
    iconColor: moduleColor("sales"),
    appGate: ["sales", "accounting"],
    currentApp: "accounting",
    path: "/accounting/sales",
    routeNames: ["SalesAnalytics"],
    children: [
      {
        title: "Orders",
        icon: "mdi-receipt-text-outline",
        iconColor: moduleColor("sales"),
        appGate: ["sales", "accounting"],
        currentApp: "accounting",
        path: "/accounting/sales/visiera-orders",
        routeNames: ["VisieraOrders"],
      },
    ],
  },

  {
    type: "group",
    key: "marketing",
    title: "Marketing",
    icon: MODULES.marketing.icon,
    iconColor: moduleColor("marketing"),
    appGate: "platforms",
    featureFlag: "marketing",
    currentApp: "marketing",
    path: "/marketing",
    routeNames: ["MarketingHome"],
    children: [
      {
        title: "Campaigns",
        icon: "mdi-bullhorn",
        iconColor: moduleColor("marketing"),
        appGate: "platforms",
        featureFlag: "campaigns",
        currentApp: "marketing",
        path: "/campaigns",
        routeNames: ["CampaignsHome", "CampaignsCalendar", "CampaignsWeeklyCalendar"],
      },
      {
        title: "Reviews",
        icon: "mdi-star",
        iconColor: moduleColor("marketing"),
        appGate: "platforms",
        featureFlag: "reviews",
        currentApp: "marketing",
        path: "/reviews",
        routeNames: [
          "ReviewsLanding",
          "ReviewsToday",
          "ReviewsHome",
          "ReviewsClassic",
          "ReviewsImport",
          "ReviewsByPlatformLocation",
          "ReviewsCalendar",
          "ReviewAssignments",
        ],
      },
      {
        title: "Disputes",
        icon: "mdi-file-document-outline",
        iconColor: moduleColor("marketing"),
        appGate: "platforms",
        featureFlag: "disputes",
        currentApp: "marketing",
        path: "/disputes",
        routeNames: ["DisputesHome"],
      },
    ],
  },

  {
    type: "group",
    key: "menu",
    title: "Menu",
    // The page the header opens — the menu-items list (Maurizio, 2026-09-10).
    subtitle: "Menu Items",
    icon: MODULES.menu.icon,
    iconColor: moduleColor("menu"),
    appGate: "menu",
    currentApp: "menu",
    path: "/menu",
    routeNames: ["MenuHome"],
    children: [
      // The group's own page, listed explicitly like every other entry
      // (Maurizio, 2026-09-10).
      {
        title: "Menu Items",
        icon: "mdi-silverware-fork-knife",
        iconColor: moduleColor("menu"),
        appGate: "menu",
        currentApp: "menu",
        path: "/menu",
        routeNames: ["MenuHome"],
      },
      {
        title: "Ingredients",
        icon: "mdi-food-apple",
        iconColor: moduleColor("menu"),
        appGate: "menu",
        currentApp: "menu",
        path: "/menu/ingredients",
        routeNames: ["MenuIngredients"],
      },
      {
        title: "Recipes",
        icon: "mdi-file-document-outline",
        iconColor: moduleColor("menu"),
        appGate: "menu",
        currentApp: "menu",
        path: "/menu/recipes",
        routeNames: ["MenuRecipes"],
      },
      {
        title: "Packaging",
        icon: "mdi-package-variant-closed",
        iconColor: moduleColor("menu"),
        appGate: "menu",
        currentApp: "menu",
        path: "/menu/packaging",
        routeNames: ["MenuPackaging"],
      },
      {
        title: "Categories",
        icon: "mdi-tag-multiple",
        iconColor: moduleColor("menu"),
        appGate: "menu",
        currentApp: "menu",
        path: "/menu/categories",
        routeNames: ["MenuCategories"],
      },
      {
        title: "Combos",
        icon: "mdi-food-variant",
        iconColor: moduleColor("menu"),
        appGate: "menu",
        currentApp: "menu",
        path: "/menu/combos",
        routeNames: ["MenuCombos"],
      },
    ],
  },

  {
    type: "leaf",
    key: "assets",
    title: "Assets",
    icon: MODULES.assets.icon,
    iconColor: moduleColor("assets"),
    appGate: "assets",
    currentApp: "assets",
    path: "/assets",
    routeNames: ["AssetsHome"],
  },

  {
    type: "group",
    key: "hr",
    title: "HR and Contacts",
    icon: MODULES.hr.icon,
    iconColor: moduleColor("hr"),
    appGate: ["hr", "hr-management"],
    currentApp: "hr",
    path: "/hr",
    routeNames: ["HrHome"],
    children: [
      {
        title: "Employees",
        icon: "mdi-account-group",
        iconColor: moduleColor("hr"),
        // "hr" is the grantable code; "hr-management" is retired but still held
        // by older accounts (2026-09-10 — an hr-only user never saw this entry).
        appGate: ["hr", "hr-management"],
        currentApp: "hr",
        path: "/hr/employees",
        routeNames: ["HrEmployees"],
      },
      {
        title: "Attendance",
        icon: "mdi-calendar-check",
        iconColor: moduleColor("hr"),
        appGate: ["hr", "hr-management"],
        currentApp: "hr",
        path: "/hr/attendance",
        routeNames: ["HrAttendance"],
      },
      {
        title: "Payroll",
        payroll: true, // salaries on screen: payroll readers only
        icon: MODULES.payroll.icon,
        iconColor: moduleColor("payroll"),
        appGate: ["hr-payroll", "accounting", "gl"],
        currentApp: "hr",
        path: "/hr/payroll",
        routeNames: ["HrPayroll"],
      },
      {
        title: "Contacts",
        icon: MODULES.contacts.icon,
        iconColor: moduleColor("contacts"),
        appGate: "contacts",
        currentApp: "hr",
        path: "/contacts",
        routeNames: ["ContactsHome"],
      },
    ],
  },

  {
    type: "leaf",
    key: "tasks",
    title: "Tasks",
    icon: MODULES.tasks.icon,
    iconColor: moduleColor("tasks"),
    appGate: "tasks",
    featureFlag: "tasks",
    currentApp: "tasks",
    path: "/tasks",
    routeNames: ["TasksHome"],
  },

  // Fusion Eat left this menu on 16 Oct 2026: it has its own site, the Fusion
  // Meals admin (admin.fusion-eat.com, config/mealsAdmin.js).

  // ── Administration ─────────────────────────────────────────────────────────
  // Users, Audit Logs, Documentation, then System Settings (rendered right
  // after these from its own export, NavigationDrawer.vue). Documentation has
  // no gate, so every user sees this label with at least that entry under it.
  { type: "subheader", key: "sh-admin", title: "Administration" },

  // Users and Audit Logs sit OUTSIDE System Settings (Maurizio, 2026-09-05):
  // they stopped being set-once configuration the day access became a per-person
  // thing — granting warehouse-ops and then reading back who recorded what are
  // routine jobs, not settings. Still appadmin-only, same pages, same routes.
  {
    type: "leaf",
    key: "users",
    title: "Users",
    icon: "mdi-account-group",
    iconColor: "blue-grey",
    requiresAppAdmin: true,
    path: "/settings/users",
    routeNames: ["UsersManagement"],
  },
  {
    type: "leaf",
    key: "audit-logs",
    title: "Audit Logs",
    icon: "mdi-history",
    iconColor: "blue-grey",
    requiresAppAdmin: true,
    path: "/settings/audit-logs",
    routeNames: ["AuditLogs"],
  },

  // Visible to every authenticated user (no appGate) — the page itself
  // gates its own sections and the route guard blocks non-admins, matching
  // today's exact behavior (no v-if on this item at all).
  {
    type: "leaf",
    key: "docs",
    title: "Documentation",
    icon: "mdi-book-open-variant",
    path: "/settings/docs",
    routeNames: ["Documentation"],
  },
];

// System Settings — appadmin only. Kept as a separate export (rendered
// after the admin-only divider) rather than folded into navGroups, exactly
// matching today's structure (the divider + admin-only block).
export const settingsGroup = {
  type: "group",
  key: "settings",
  title: "System Settings",
  icon: "mdi-cog",
  requiresAppAdmin: true,
  path: "/settings",
  routeNames: ["Settings"],
  // Users and Audit Logs used to live here — promoted to top-level entries.
  children: [
    {
      title: "Options Management",
      icon: "mdi-format-list-bulleted",
      path: "/settings/options",
      routeNames: ["OptionsManagement"],
    },
    {
      // One screen for platform × brand × place, dates and store ids —
      // replaced Business Rules + Restaurant Mappings (2026-09-23).
      title: "Outlets",
      icon: "mdi-storefront-outline",
      path: "/settings/outlets",
      routeNames: ["OutletsSettings"],
    },
    {
      title: "Platform Cost Coverage",
      icon: "mdi-table-cog",
      path: "/settings/platform-cost-coverage",
      routeNames: ["PlatformCostCoverageSettings"],
    },
    {
      title: "Modules",
      icon: "mdi-toggle-switch-outline",
      path: "/settings/modules",
      routeNames: ["ModulesSettings"],
    },
    {
      title: "Tags",
      icon: "mdi-tag-multiple",
      path: "/settings/tags",
      routeNames: ["TagsManagement"],
    },
    {
      title: "Task Settings",
      icon: "mdi-clipboard-check-outline",
      featureFlag: "tasks",
      path: "/settings/tasks",
      routeNames: ["TasksSettings"],
    },
    {
      title: "Address Book Settings",
      icon: "mdi-cog-outline",
      path: "/settings/address-book",
      routeNames: ["AddressBookSettings"],
    },
    {
      title: "Reviews Admin",
      icon: "mdi-cog",
      path: "/settings/reviews-admin",
      featureFlag: "reviews",
      routeNames: ["ReviewsAdmin"],
    },
    {
      title: "Asset Settings",
      icon: "mdi-cog",
      path: "/settings/assets",
      routeNames: ["AssetSettings"],
    },
    // Both previously orphaned — reachable only by typed URL before this reorg.
    {
      title: "Account Managers",
      icon: "mdi-account-tie",
      path: "/settings/account-managers",
      featureFlag: "reviews",
      routeNames: ["AccountManagersManagement"],
    },
    {
      title: "Platform logins",
      icon: "mdi-key",
      path: "/contacts/credentials", // the credentials store (Contacts); the old Settings page had no backend
      routeNames: ["ContactsCredentials"],
    },
  ],
};

/**
 * Is a single entry (leaf, group's own primary destination, or a child)
 * visible to this user? Mirrors the exact bypass/gate logic every existing
 * hasX computed in NavigationDrawer.vue used before this reorg.
 */
export function isEntryVisible(entry, { allowedApps, isAppAdmin, user }) {
  // A flagged-off feature is hidden for everybody, appadmins included (2026-10-09:
  // the flag used to be checked after the appadmin bypass, so it never hid anything from them)
  if (entry.featureFlag && !isFeatureEnabled(entry.featureFlag)) return false;
  if (isAppAdmin) return true;
  if (entry.requiresAppAdmin) return false;
  // Salaries on screen: the payroll grant or an admin who keeps the books (2026-11-04)
  if (entry.payroll && !canReadPayroll(user)) return false;
  if (entry.appGate) {
    const gates = Array.isArray(entry.appGate) ? entry.appGate : [entry.appGate];
    // allowedApps = the codes held to change OR only to look ("gl:view" counts as gl)
    if (!gates.some((g) => allowedApps.includes(g))) return false;
    // A shortcut to typing work: hidden from whoever may only look (view-only grant,
    // or a Viewer). "grant" = a full grant is enough, whatever the role — Direct
    // Upload's cashiers are plain users on purpose.
    if (entry.editOnly === true && !canEditApp(user, gates)) return false;
    if (entry.editOnly === "grant" && !holdsApp(user, gates, { reading: false })) return false;
  }
  return true;
}

/** Does this group have at least one visible child? (gates the chevron affordance) */
export function hasVisibleChildren(group, ctx) {
  return (group.children || []).some((c) => isEntryVisible(c, ctx));
}

/**
 * A group's parent row is visible if its own primary page is, OR any child is.
 * An accordion group (no path) has no page of its own, so its children decide —
 * otherwise a gate-less header would pass isEntryVisible() and a user with none
 * of the children would see an empty "Daily operations".
 */
export function isGroupVisible(group, ctx) {
  if (!group.path) return hasVisibleChildren(group, ctx);
  return isEntryVisible(group, ctx) || hasVisibleChildren(group, ctx);
}
